import { motion } from "framer-motion";
import { ChevronRightIcon } from "@heroicons/react/24/outline";

import {
  FundingMethod,
  FundingMethodId,
  isMethodAvailable,
} from "../../../../utils/fundingMethods";

interface MethodSelectListProps {
  methods: FundingMethod[];
  currency: string | null;
  onSelect: (id: FundingMethodId) => void;
}

export default function MethodSelectList({
  methods,
  currency,
  onSelect,
}: MethodSelectListProps) {
  if (methods.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-gray-500">
        No funding methods are available for this currency yet.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">
        Choose how you&apos;d like to add money.
      </p>

      {methods.map((method, index) => {
        const Icon = method.icon;
        const isComingSoon = !isMethodAvailable(method, currency);

        return (
          <motion.button
            key={method.id}
            type="button"
            onClick={() => onSelect(method.id)}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="group flex w-full items-center gap-3 rounded-xl border border-gray-100 p-4 text-left transition-colors hover:border-primary hover:bg-primary-50/50"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary">
              <Icon className="h-6 w-6" />
            </span>

            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {method.label}
                </span>

                {isComingSoon && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                    Soon
                  </span>
                )}
              </span>
              <span className="block truncate text-xs text-gray-400">
                {method.description}
              </span>
            </span>

            <ChevronRightIcon className="h-5 w-5 shrink-0 text-gray-300 transition-colors group-hover:text-primary" />
          </motion.button>
        );
      })}
    </div>
  );
}
